import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    Input,
    OnInit,
    TemplateRef,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { KalDate, KalDialogService } from '@kalidea/kaligraphi';
import { pickBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { TernaryValuePipe } from 'src/app/_shared/pipes/ternary-value.pipe';
import { AdvancedRule, AdvancedRuleService } from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { AllocationsContext, AllocationService } from 'src/app/_shared/services/allocations/detail/allocation.service';
import { ReferenceYearsState } from 'src/app/_shared/stores/reference-years/reference-years.state';
import { UserState } from 'src/app/_user/store/user/user.state';
import { TypeActivite } from 'src/app/board/activity/activity.constants';
import {
    SubventionAllocationState
} from 'src/app/board/subvention/stores/subvention-allocation/subvention-allocation-state';
import { arrayEmpty, arrayNotEmpty } from 'src/app/utils/array.utils';
import { getISODate, getKalDateFromObject, getUTCDate } from 'src/app/utils/date.utils';
import { recursiveDeleteEmptyProperties } from 'src/app/utils/object.utils';
import { constants } from 'src/environments/constants';
import {
    AttributionBudgetModelDto,
    ContactParamModel,
    EditerExecutionAttributionAutoModel,
    MoteurRegleModelDtoInput,
    RegleAvanceeModelDto,
    StatusDtoEnum,
    TypesRegleEnum
} from 'src/graphql/generated-types';

export interface AllocationInformations extends AttributionBudgetModelDto {
    from: string;
    fromMail: string;
}

@Component({
    selector: 'app-shared-allocation-edit',
    templateUrl: './shared-allocation-edit.component.html',
    styleUrls: ['./shared-allocation-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationEditComponent extends BaseComponent implements OnInit {

    tabControl = new FormControl<number>(0);

    form: FormGroup;

    allocation: AttributionBudgetModelDto;

    @ContentChild('informationsTemplate')
    informationsTemplateRef: TemplateRef<any>;

    loading$ = this.allocationService.loading$;

    title: string = null;

    AllocationsContext = AllocationsContext;

    ruleTypeEnum = TypesRegleEnum;

    @Input() activityType: TypeActivite | null = null;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly layoutService: LayoutService,
                private readonly dialogService: KalDialogService,
                private readonly advancedRuleService: AdvancedRuleService,
                private readonly allocationService: AllocationService) {
        super(cdr);
    }

    get automatedAllocationExecution(): EditerExecutionAttributionAutoModel {
        return this.snapshot(SubventionAllocationState.automatedAllocationExecution);
    }

    private get informationsValue(): Record<string, any> {
        return this.form.get('informations')?.value;
    }

    get isNominative(): boolean {
        return this.informationsValue?.IsNominative ?? false;
    }

    get type(): string {
        return this.allocationService.type;
    }

    get operationReferenceDateId(): number {
        return this.getReferenceDateIdByCode('OPE');
    }

    get nowReferenceDateId(): number {
        return this.getReferenceDateIdByCode('NOW');
    }

    private get whoDefaultValues(): { ModeAccess: string, IdTypeDateRefAge: number, IdtypeRefAnc: number } {
        return {
            ModeAccess: 'ANC',
            IdTypeDateRefAge: this.nowReferenceDateId,
            IdtypeRefAnc: this.nowReferenceDateId,
        };
    }

    private get hasWhoSimpleModeId(): boolean {
        return !!this.allocation?.MoteurRegleAcc?.ModeleRegleAcc?.Id;
    }

    hasRuleError(opts: {
        simpleRuleControlName: string,
        isAdvancedRuleControlName: string,
        advancedRuleControlName: string
    }): boolean {
        if(!!this.form.get(opts.isAdvancedRuleControlName).value) {
            return !!this.form.get(opts.advancedRuleControlName).errors;
        }
        return !!this.form.get(opts.simpleRuleControlName).errors;
    }

    hasError(errorName: string): boolean {
        return !!this.form.get(errorName).errors;
    }

    save(): void {
        const allocation = this.normalizeAllocation();
        const automatedExecution = this.normalizeAutomatedExecution();

        this.watch$ = this.allocationService.save({
            allocation,
            automatedExecution
        });
    }

    cancel(): void {
        const allocationId = this.allocation?.Id;

        this.watch$ = !!allocationId
            ? this.navigateToDetail(allocationId)
            : this.navigateToList();
    }

    navigateToList(): Observable<any> {
        return this.allocationService.navigateToList();
    }

    navigateToDetail(allocationId?: number): Observable<any> {
        return this.allocationService.navigateToDetail(allocationId);
    }

    createForm(): void {
        this.form = this.fb.group({
            Status: [StatusDtoEnum.Inserted],
            Id: [0],
            informations: [null],
            howMuch: [{
                IdTypeDateRefPond: this.operationReferenceDateId,
            }],
            isHowMuchAdvanced: [false],
            howMuchAdvanced: [null],
            who: [this.whoDefaultValues],
            isWhoAdvanced: [false],
            whoAdvanced: [null],
            DateExec: [null],
            DateDernierMAJ: [null],
            IsCloture: [false],
            MoteurRegleAcc: [null],
            MoteurRegleSubQta: [null],
            IsMasquee: [false],
            DateEvenement: [null],
        });
    }

    get switchToAdvancedModeLabel(): string {
        const isWhoTab = this.tabControl.value === 1;
        const simpleModeLabel = 'Passer au mode guidé';
        const advancedModeLabel = 'Passer au mode avancé';

        if(isWhoTab) {
            return TernaryValuePipe.format(!!this.form.value.isWhoAdvanced, simpleModeLabel, advancedModeLabel);
        }

        return TernaryValuePipe.format(!!this.form.value.isHowMuchAdvanced, simpleModeLabel, advancedModeLabel);
    }

    isRuleAdvanced(ruleType: TypesRegleEnum): boolean {
        switch (ruleType) {
            case TypesRegleEnum.Accessibilite:
                return this.form.value.isWhoAdvanced;
            case TypesRegleEnum.Subvention:
                return this.form.value.isHowMuchAdvanced;
            default:
                return false;
        }
    }

    advancedRuleId(ruleType: TypesRegleEnum): number | null {
        switch (ruleType) {
            case TypesRegleEnum.Accessibilite:
                return this.allocation?.MoteurRegleAcc?.RegleAvancee?.Id || this.allocation?.MoteurRegleAcc?.Id || 0;
            case TypesRegleEnum.Subvention:
                return this.allocation?.MoteurRegleSubQta?.RegleAvancee?.Id || this.allocation?.MoteurRegleSubQta?.Id || 0;
            default:
                return null;
        }
    }

    switchToAdvancedMode(): void {
        const isWhoTab = this.tabControl.value === 1;

        const [
            isAdvancedControlName,
            simpleRuleControlName,
            advancedRuleControlName
        ] = isWhoTab
            ? ['isWhoAdvanced', 'who', 'whoAdvanced']
            : ['isHowMuchAdvanced', 'howMuch', 'howMuchAdvanced'];

        const isRuleAdvancedControl = this.form.get(isAdvancedControlName);

        isRuleAdvancedControl.patchValue(!isRuleAdvancedControl.value);

        this.form.get(simpleRuleControlName)[!!isRuleAdvancedControl.value ? 'disable' : 'enable']({emitEvent: false});
        this.form.get(advancedRuleControlName)[!!isRuleAdvancedControl.value ? 'enable' : 'disable']({emitEvent: false});

        // Dans le cas de l'onglet "Pour qui" :
        // si on repasse en mode guidé alors que le mode initial (lors de l'arrivée sur le mode edit) était "avancé"
        // il faut mettre les valeurs par défaut au mode guidé
        if ([!isRuleAdvancedControl.value, isWhoTab, !this.hasWhoSimpleModeId].every(e => e)) {
            this.form.get(simpleRuleControlName).patchValue(this.whoDefaultValues, {emitEvent: false});
        }
    }

    private getFromInfo(): { from: string, fromMail: string | number } {
        if (!this.automatedAllocationExecution) {
            const delegationId = this.store.selectSnapshot(UserState.currentDelegation)?.Id;
            return {from: 'ce', fromMail: delegationId};
        }

        const {
            EmailLibre,
            IdCE,
            IdCommission,
            IdUser
        } = this.automatedAllocationExecution?.ContactParam || {};

        if (!!EmailLibre) {
            return {from: 'freeEntry', fromMail: EmailLibre};
        }
        if (!!IdCE) {
            return {from: 'ce', fromMail: IdCE};
        }
        if (!!IdUser) {
            return {from: 'user', fromMail: IdUser};
        }

        return {from: 'commission', fromMail: IdCommission};
    }

    private updateForm(allocation: AttributionBudgetModelDto): void {
        const {
            Libelle,
            Code,
            IdEnveloppe,
            DateValeurDefaut,
            IdAnneeRef,
            IsNominative,
            Description,
            AttribTgExec,
            ArticleModele,

            IdCompteChargeAbondement,
            IdComptePMVAbondement,
            IdJournalAbondement,

            MoteurRegleSubQta,
            MoteurRegleAcc,
            ListeClesRepartition,

            IsMasquee,
            DateEvenement,
        } = allocation || {
            IsNominative: false
        };

        const ruleModelSubQuotaValueList = MoteurRegleSubQta?.ModeleRegleSubQta?.ListeModeleRegleSubQuotaValModele?.RegleSubQuotaListeValModelDTO || [];

        const rulesList = ruleModelSubQuotaValueList.filter(rule => !!rule.ChpLstValId);
        const defaultValuesList = ruleModelSubQuotaValueList.filter(rule => !rule.ChpLstValId);

        this.form.patchValue({
            Id: allocation?.Id,
            informations: {
                Libelle,
                Code,
                IdEnveloppe,
                IdAnneeRef,
                DateValeurDefaut,
                IsNominative,
                Description,
                AttribTgExec,
                ArticleModele,

                IdCompteChargeAbondement,
                IdComptePMVAbondement,
                IdJournalAbondement,
                ListeClesRepartition: ListeClesRepartition?.CleRepartitionModelDTO || [],

                IsMasquee,
                DateEvenement,

                IsExecutionAuto: this.automatedAllocationExecution?.IsExecutionAuto,
                FrequenceExecution: this.automatedAllocationExecution?.FrequenceExecution,
                NeedSendAlwaysReport: this.automatedAllocationExecution?.NeedSendAlwaysReport,

                ...this.getFromInfo()
            },
            howMuch: {
                ...MoteurRegleSubQta?.ModeleRegleSubQta || {},
                ListeRegle : arrayEmpty(rulesList) ? defaultValuesList : rulesList,
                ListeValeursDefault: defaultValuesList
            },
            isHowMuchAdvanced: allocation?.MoteurRegleSubQta?.IsRegleAvancee,
            who: allocation?.MoteurRegleAcc?.ModeleRegleAcc,
            isWhoAdvanced: !!allocation?.MoteurRegleAcc?.IsRegleAvancee,
            Status: StatusDtoEnum.Updated,
            MoteurRegleAcc,
            MoteurRegleSubQta
        });

        if (allocation?.AttribTgExec) {
            ['howMuch', 'who', 'howMuchAdvanced', 'whoAdvanced'].forEach(controlName => this.form.get(controlName).disable());
        } else {
            const isWhoAdvanced = this.form.value.isWhoAdvanced;
            const isHowMuchAdvanced = this.form.value.isHowMuchAdvanced;

            const rules = {
                'who': !!isWhoAdvanced ? 'disable' : 'enable',
                'whoAdvanced': !!isWhoAdvanced ? 'enable' : 'disable',
                'howMuch': !!isHowMuchAdvanced ? 'disable' : 'enable',
                'howMuchAdvanced': !!isHowMuchAdvanced ? 'enable' : 'disable'
            };

            Object.entries(rules).forEach(([controlName, value]) => this.form.get(controlName)[value]());
        }
    }

    private getReferenceDateIdByCode(code: string): number {
        return this.snapshot(ReferenceYearsState.referenceMotorDatesList)
            .find(({Code}) => Code === code)?.Id ?? null;
    }

    private parseAdvancedRule(rule: AdvancedRule & {
        TypeSP: string,
        Precision: number,
        TypeArrondi: string,
        IsSubventionEnveloppeComplementaire: boolean,
        IdEnveloppeComplementaire: number
    }): RegleAvanceeModelDto {
        const normalizedRule = this.advancedRuleService.normalize(rule);

        return {
            // #51935 #51936 (déplacement du commentaire de #50735 et #51504)
            // [FWA] :
            // dans certains cas, certaines valeurs sont nulles alors qu'elles ne le devraient pas et génèrent une erreur,
            // elles doivent donc être retirées pour empêcher cela.
            // Les règles enfants sont, elles aussi concernés. Mais dans leurs cas les valeurs à 0 doivent être épargnées
            ...pickBy(normalizedRule),
            ReglesEnfants: {
                RegleAvanceeModelDTO: (normalizedRule.ReglesEnfants.RegleAvanceeModelDTO || normalizedRule.ReglesEnfants as RegleAvanceeModelDto[]).map(childRule => {
                    recursiveDeleteEmptyProperties(childRule);
                    return childRule;
                })
            }
        };
    }

    private whoCriteria(): MoteurRegleModelDtoInput {
        const {
            who,
            whoAdvanced,
            isWhoAdvanced
        } = this.form.getRawValue();

        if(!!who) {
            delete who.Valeur;
            const list = who.ListeModeleRegleAccListeModele?.RegleAccListeValModelDTO;
            if (arrayNotEmpty(list)) {
                list.map(e => {
                    delete e.Valeur;
                    return e;
                });
            }
        }

        return {
            ModeleRegleAcc: !who
                ? null
                : {
                    ...who,
                    Status: this.hasWhoSimpleModeId
                        ? StatusDtoEnum.Updated
                        : StatusDtoEnum.Inserted
                },
            IsRegleAvancee: isWhoAdvanced,
            RegleAvancee: isWhoAdvanced ? this.parseAdvancedRule(whoAdvanced) : null
        };
    }

    private normalizeAllocation(): AttributionBudgetModelDto {
        const {
            informations,
            howMuch: {
                ListeRegle,
                ...howMuch
            },
            isHowMuchAdvanced,
            howMuchAdvanced,
            Status,
            Id
        } = this.form.getRawValue();

        let virtuelWalletCriteria: AttributionBudgetModelDto = {};

        if (this.allocationService.type === AllocationsContext.PMV) {
            virtuelWalletCriteria = {
                IdCompteChargeAbondement: informations.IdCompteChargeAbondement,
                IdComptePMVAbondement: informations.IdComptePMVAbondement,
                IdJournalAbondement: informations.IdJournalAbondement,
            };
        }

        const ListeClesRepartition = informations.ListeClesRepartition;

        const date = getKalDateFromObject(informations, 'DateValeurDefaut');
        const eventDate = getKalDateFromObject(informations, 'DateEvenement');

        return {
            Id,
            Status,
            Libelle: informations.Libelle,
            Code: informations.Code,
            IdEnveloppe: informations.IdEnveloppe,
            DateValeurDefaut: getUTCDate(date, {shouldResetTime: true}),
            IdAnneeRef: informations.IdAnneeRef,
            IsNominative: informations.IsNominative,
            Description: informations.Description,
            AttribTgExec: informations.AttribTgExec,
            ArticleModele: informations.ArticleModele,

            ListeClesRepartition: arrayNotEmpty(ListeClesRepartition?.CleRepartitionModelDTO) ? ListeClesRepartition : null,
            ...virtuelWalletCriteria,

            MoteurRegleAcc: {
                Status,
                Id: this.allocation?.MoteurRegleAcc?.Id || 0,
                LibelleRegle: this.allocation?.MoteurRegleAcc?.LibelleRegle || `Accessibilité ${informations.Libelle}`.trim().substring(0, 50),
                ...this.whoCriteria()
            },

            MoteurRegleSubQta: {
                Status,
                Id: this.allocation?.MoteurRegleSubQta?.Id || 0,
                LibelleRegle: this.allocation?.MoteurRegleSubQta?.LibelleRegle || `Subvention ${informations.Libelle}`.trim().substring(0, 50),
                TypeRegle: constants.rulesType.subv,
                ModeleRegleSubQta: {
                    RglModeleSQTypeRegle: 'S',
                    ...howMuch,
                    Status,
                    IsNominatif: informations.IsNominative,
                    IsPourChaqueOD: true
                },
                IsRegleAvancee: isHowMuchAdvanced,
                RegleAvancee: !!isHowMuchAdvanced ? this.parseAdvancedRule(howMuchAdvanced) : null
            },
            DateDernierMAJ: getISODate(new KalDate()),
            IsMasquee: informations.IsMasquee,
            DateEvenement: getUTCDate(eventDate, {shouldResetTime: true}),
        };
    }

    private normalizeAutomatedExecution(): EditerExecutionAttributionAutoModel {
        const {
            informations,
            Id
        } = this.form.value;

        const idContactKeys = {
            IdCE: 'ce',
            IdCommission: 'commission',
            IdUser: 'user',
        };

        const ContactParam: ContactParamModel = {
            EmailLibre: informations.from === 'freeEntry' ? informations.fromMail : null,
        };

        Object.keys(idContactKeys).forEach(key =>
            ContactParam[key] = informations.from === idContactKeys[key]
                ? +informations.fromMail || this.getFromInfo().fromMail
                : null
        );

        return {
            IdAttrib: Id,
            IsExecutionAuto: informations.IsExecutionAuto,
            FrequenceExecution: informations.FrequenceExecution,
            NeedSendAlwaysReport: informations.NeedSendAlwaysReport,
            ContactParam
        };
    }

    ngOnInit(): void {
        const typeLabel = this.type === AllocationsContext.PMV ? 'd\'un abondement' : 'd\'une attribution';

        this.createForm();

        this.watch$ = this.allocationService.initialize$().pipe(
            switchMap(() => this.allocationService.allocation$),
            filter((allocation) => !!allocation?.Id),
            tap(allocation => {
                this.allocation = allocation;
                this.updateForm(allocation);
                this.title = (!!this.allocation?.Id ? 'Modification ' : 'Création ') + typeLabel;
                this.cdr.markForCheck();
            })
        );
    }
}
